import React, { useEffect, useState, useCallback } from "react";
import * as S from "./AdminDepartments.styled";
import { universityService, Department } from "services/universityService";
import { adminDepartmentService } from "services/adminDepartmentService";

const AdminDepartments: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  /* ── 데이터 로드 ── */
  const fetchDepartments = useCallback(async () => {
    setIsLoading(true);
    try {
      const universityId = Number(localStorage.getItem('adminUniversityId'));
      const list = await universityService.getDepartments(universityId);
      setDepartments(list);
    } catch {
      // 에러 시 빈 목록 유지
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  /* ── 검색 필터 ── */
  const filtered = departments.filter((d) =>
    d.name.toLowerCase().includes(query.toLowerCase())
  );

  /* ── 모달 열기 ── */
  const openAdd = () => {
    setEditingDept(null);
    setInputValue("");
    setInputError("");
    setIsModalOpen(true);
  };

  const openEdit = (dept: Department) => {
    setEditingDept(dept);
    setInputValue(dept.name);
    setInputError("");
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  /* ── 저장 ── */
  const handleSave = async () => {
    if (!inputValue.trim()) {
      setInputError("학과명을 입력해주세요.");
      return;
    }
    setIsSaving(true);
    try {
      if (editingDept) {
        await adminDepartmentService.updateDepartment(editingDept.id, { name: inputValue.trim() });
      } else {
        const universityId = Number(localStorage.getItem('adminUniversityId'));
        await adminDepartmentService.createDepartment(universityId, { name: inputValue.trim() });
      }
      closeModal();
      fetchDepartments();
    } catch {
      setInputError("저장에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  /* ── 삭제 ── */
  const handleDelete = async (e: React.MouseEvent, departmentId: number) => {
    e.stopPropagation();
    if (!window.confirm("학과를 삭제하시겠습니까?")) return;
    setDeletingId(departmentId);
    try {
      await adminDepartmentService.deleteDepartment(departmentId);
      fetchDepartments();
    } catch {
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") closeModal();
  };

  /* ── 렌더 ── */
  return (
    <>
      <S.PageHeader>
        <S.PageTitle>전체 학과 목록</S.PageTitle>
        <S.AddButton onClick={openAdd}>+ 학과 추가</S.AddButton>
      </S.PageHeader>

      <S.Toolbar>
        <S.SearchWrap>
          <S.SearchIcon>🔍</S.SearchIcon>
          <S.SearchInput
            placeholder="학과명으로 검색..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </S.SearchWrap>
        <S.CountText>
          {isLoading ? "로딩 중..." : `총 ${filtered.length}개 학과`}
        </S.CountText>
      </S.Toolbar>

      <S.Card>
        <S.Table>
          <S.Thead>
            <tr>
              <S.Th width="60px">#</S.Th>
              <S.Th>학과명</S.Th>
              <S.Th width="140px"></S.Th>
            </tr>
          </S.Thead>
          <S.Tbody>
            {!isLoading && filtered.length === 0 ? (
              <S.EmptyRow>
                <S.EmptyCell colSpan={3}>
                  {query ? `"${query}"에 해당하는 학과가 없습니다.` : "등록된 학과가 없습니다."}
                </S.EmptyCell>
              </S.EmptyRow>
            ) : (
              filtered.map((dept, index) => (
                <S.Tr key={dept.id}>
                  <S.Td>
                    <S.IndexCell>{index + 1}</S.IndexCell>
                  </S.Td>
                  <S.Td>{dept.name}</S.Td>
                  <S.Td>
                    <S.ActionButtons>
                      <S.EditButton onClick={(e) => { e.stopPropagation(); openEdit(dept); }}>
                        수정
                      </S.EditButton>
                      <S.DeleteButton
                        disabled={deletingId === dept.id}
                        onClick={(e) => handleDelete(e, dept.id)}
                      >
                        {deletingId === dept.id ? "삭제 중..." : "삭제"}
                      </S.DeleteButton>
                    </S.ActionButtons>
                  </S.Td>
                </S.Tr>
              ))
            )}
          </S.Tbody>
        </S.Table>
      </S.Card>

      {/* ── 추가 / 수정 모달 ── */}
      {isModalOpen && (
        <S.Overlay onClick={closeModal}>
          <S.Modal onClick={(e) => e.stopPropagation()}>
            <S.ModalTitle>{editingDept ? "학과 수정" : "학과 추가"}</S.ModalTitle>

            <S.ModalLabel htmlFor="deptName">학과명</S.ModalLabel>
            <S.ModalInput
              id="deptName"
              placeholder="예) 컴퓨터공학과"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setInputError("");
              }}
              onKeyDown={handleKeyDown}
              hasError={!!inputError}
              autoFocus
            />
            {inputError && <S.ModalError>{inputError}</S.ModalError>}

            <S.ModalActions>
              <S.CancelButton onClick={closeModal} disabled={isSaving}>취소</S.CancelButton>
              <S.SaveButton onClick={handleSave} disabled={isSaving}>
                {isSaving ? "저장 중..." : editingDept ? "저장" : "추가"}
              </S.SaveButton>
            </S.ModalActions>
          </S.Modal>
        </S.Overlay>
      )}
    </>
  );
};

export default AdminDepartments;
